import { createContext, Dispatch } from 'react'

import { ReducerAction, IReducerContextProps } from 'utility-tools/module/reducers'
import { TOverrideType } from 'utility-tools/module/types'
import { ICurrencyDto } from '../../../coins'

interface IAvailablePairs {
    fromPairs: object
    toPairs: object
    allAvailablePairs: object[]
}

type TAvailableCurrenciesState = {
    currencies: ICurrencyDto[]
    availablePairs: IAvailablePairs
}

const initAvailableCurrenciesState: TAvailableCurrenciesState = {
    currencies: [],
    availablePairs: {
        fromPairs: {},
        toPairs: {},
        allAvailablePairs: [],
    },
}

enum AvailableCurrenciesReducerAction {
    SET_CURRENCIES = 'SET_CURRENCIES',
    SET_AVAILABLE_PAIRS = 'SET_AVAILABLE_PAIRS',
    BULK_CHANGE_STATE = 'BULK_CHANGE_STATE',
}

class SetCurrenciesAction extends ReducerAction<ICurrencyDto[]> {
    constructor(currencies) {
        super(AvailableCurrenciesReducerAction.SET_CURRENCIES, currencies)
    }
}

class SetAvailablePairsAction extends ReducerAction<IAvailablePairs> {
    constructor(availablePairs) {
        super(AvailableCurrenciesReducerAction.SET_AVAILABLE_PAIRS, availablePairs)
    }
}

class SetAvailableCurrenciesBulkAction extends ReducerAction<Partial<TAvailableCurrenciesState>> {
    constructor(newState) {
        super(AvailableCurrenciesReducerAction.BULK_CHANGE_STATE, newState)
    }
}

const availableCurrenciesReducer = (
    state: TAvailableCurrenciesState = initAvailableCurrenciesState,
    action: SetCurrenciesAction | SetAvailablePairsAction | SetAvailableCurrenciesBulkAction,
): TAvailableCurrenciesState => {
    const { type, payload } = action

    switch (type) {
        case AvailableCurrenciesReducerAction.SET_CURRENCIES: {
            return <TAvailableCurrenciesState>{
                ...state,
                currencies: payload,
            }
        }

        case AvailableCurrenciesReducerAction.SET_AVAILABLE_PAIRS: {
            return <TAvailableCurrenciesState>{
                ...state,
                availablePairs: payload,
            }
        }

        case AvailableCurrenciesReducerAction.BULK_CHANGE_STATE: {
            return <TAvailableCurrenciesState>{
                ...state,
                ...(payload as Partial<TAvailableCurrenciesState>),
            }
        }

        default:
            return state
    }
}

type TAvailableCurrenciesContext = TOverrideType<
    IReducerContextProps,
    {
        state: TAvailableCurrenciesState
        dispatchAction?: Dispatch<
            SetCurrenciesAction | SetAvailablePairsAction | SetAvailableCurrenciesBulkAction
        >
    }
>

const AvailableCurrenciesContext = createContext<TAvailableCurrenciesContext>({
    state: initAvailableCurrenciesState,
})

export {
    availableCurrenciesReducer,
    initAvailableCurrenciesState,
    AvailableCurrenciesContext,
    SetCurrenciesAction,
    SetAvailablePairsAction,
    SetAvailableCurrenciesBulkAction,
}
